/**
 * @module
 *
 * Generic Proptypes types for use throughout the application.
 */

import { ISO_8601_DATE_STR, UUID_REGEX } from '@whistler-ui/k2/constants';

/**
 * Used to thrown an error message if a Proptype is invalid.
 *
 * @param {string} propName - The prop name.
 * @param {string} componentName - The component name.
 * @param {string} requiredTypeName - The required type name.
 *
 * @returns {TypeError} - A new TypeError with the error message.
 */
const invalidTypeError = (propName, componentName, requiredTypeName) =>
  new TypeError(
    `Invalid prop \`${propName}\` supplied to \`${componentName}\`, expected \`${requiredTypeName}\`.`,
  );

/**
 * Basic function to handle required Proptypes by checking if the prop value is
 * not falsy.
 *
 * @param {Object} props - The props.
 * @param {string} propName - The prop name.
 * @param {string} componentName - The component name.
 */
const isRequiredHandler = (props, propName, componentName) => {
  if (!props[propName])
    throw new TypeError(
      `The prop \`${propName}\` is marked as required in \`${componentName}\`, but its value is \`${props[propName]}\`.`,
    );
};

/**
 * UUID.
 *
 * Example: 123e4567-e89b-12d3-a456-426655440000
 */
export const uuid = (props, propName, componentName) => {
  if (!UUID_REGEX.test(props[propName]))
    return invalidTypeError(propName, componentName, 'uuid');
};
uuid.isRequired = isRequiredHandler;

/**
 * ISO 8601 date string.
 *
 * Example: 2023-05-10T08:47:01.099000+00:00
 */
export const iso8601DateStr = (props, propName, componentName) => {
  if (!ISO_8601_DATE_STR.test(props[propName]))
    return invalidTypeError(propName, componentName, 'ISO 8601 date string');
};
iso8601DateStr.isRequired = isRequiredHandler;
